const STANDALONE_DISPLAY_MODE_QUERIES = [
    "(display-mode: standalone)",
    "(display-mode: fullscreen)",
    "(display-mode: minimal-ui)",
];
const IOS_UA_PATTERN = /iP(hone|ad|od)/;
const IOS_BROWSER_EXCLUSION_PATTERN = /CriOS|FxiOS/;
const MAC_UA_PATTERN = /Macintosh|Mac OS X/;
const SAFARI_UA_PATTERN = /Safari/;
const NON_SAFARI_ENGINE_PATTERN = /Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS|Android/;
/**
 * The browser adapter behind `InstallEnvironmentPort` — the composition root
 * (`system/pwa/src/main.ts`) constructs this instead of `client/ui`'s install-prompt UI (a
 * later task) touching `beforeinstallprompt`/`appinstalled`/`navigator.standalone`/
 * `navigator.getInstalledRelatedApps` inline, keeping browser I/O out of `client/ui`
 * (boundary rules B2/B3) the same way `BrowserThemeEnvironment`/`BrowserMediaPreferences` keep
 * `localStorage`/`matchMedia` out of it. See `InstallEnvironmentPort`'s own doc comment for why
 * this holds onto the captured prompt itself rather than making callers race the event.
 */
export class BrowserInstallEnvironment {
    #win;
    #matchMedia;
    #nav;
    #userAgent;
    #capturedPrompt;
    #installAvailableListeners = new Set();
    #appInstalledListeners = new Set();
    constructor(options = {}) {
        const win = options.win ?? safeGlobalWindow();
        this.#win = win;
        this.#matchMedia = options.matchMedia ??
            (typeof globalThis.matchMedia === "function" ? globalThis.matchMedia.bind(globalThis) : undefined);
        this.#nav = (options.nav ?? safeGlobalNavigator());
        this.#userAgent = options.userAgent ?? this.#nav?.userAgent ?? "";
        // Both events fire once, early in the page lifecycle — attach immediately so a prompt
        // captured before any caller has subscribed via `onInstallAvailable` isn't lost.
        try {
            win?.addEventListener("beforeinstallprompt", ((event) => {
                event.preventDefault();
                this.#capturedPrompt = event;
                for (const listener of [...this.#installAvailableListeners])
                    listener();
            }));
        }
        catch {
            // Listener registration can throw in locked-down embedders — degrade to "never
            // installable" rather than throwing out of the constructor.
        }
        try {
            win?.addEventListener("appinstalled", (() => {
                for (const listener of [...this.#appInstalledListeners])
                    listener();
            }));
        }
        catch {
            // Same degrade-quietly reasoning as above.
        }
    }
    isStandalone() {
        try {
            if (this.#matchMedia) {
                for (const query of STANDALONE_DISPLAY_MODE_QUERIES) {
                    if (this.#matchMedia(query).matches)
                        return true;
                }
            }
        }
        catch {
            // Fall through to the navigator.standalone check.
        }
        try {
            if (this.#nav?.standalone === true)
                return true;
        }
        catch {
            // Property access can throw in locked-down embedders.
        }
        return false;
    }
    instructionMode() {
        if (this.#capturedPrompt)
            return "installable";
        if (this.#isIosSafari())
            return "ios";
        if (this.#isMacSafari())
            return "safari-desktop";
        return "unsupported";
    }
    onInstallAvailable(listener) {
        if (this.#capturedPrompt) {
            listener();
        }
        this.#installAvailableListeners.add(listener);
        return () => {
            this.#installAvailableListeners.delete(listener);
        };
    }
    async promptInstall() {
        const captured = this.#capturedPrompt;
        if (!captured)
            return "unavailable";
        // One-shot: a captured `beforeinstallprompt` can only be shown once, so clear it before
        // awaiting rather than after — a concurrent call must also see it as consumed.
        this.#capturedPrompt = undefined;
        try {
            await captured.prompt();
            const choice = await captured.userChoice;
            return choice.outcome === "accepted" ? "accepted" : "dismissed";
        }
        catch {
            return "unavailable";
        }
    }
    onAppInstalled(listener) {
        this.#appInstalledListeners.add(listener);
        return () => {
            this.#appInstalledListeners.delete(listener);
        };
    }
    async checkAlreadyInstalled() {
        try {
            const getInstalledRelatedApps = this.#nav?.getInstalledRelatedApps;
            if (typeof getInstalledRelatedApps !== "function")
                return false;
            const apps = await getInstalledRelatedApps.call(this.#nav);
            if (!Array.isArray(apps) || apps.length === 0)
                return false;
            const origin = this.#safeOrigin();
            if (!origin)
                return false;
            return apps.some((app) => typeof app.url === "string" && app.url.startsWith(origin));
        }
        catch {
            return false;
        }
    }
    #isIosSafari() {
        const ua = this.#userAgent;
        const isIosUa = IOS_UA_PATTERN.test(ua) && !IOS_BROWSER_EXCLUSION_PATTERN.test(ua);
        if (isIosUa)
            return true;
        // iPadOS 13+ identifies as "MacIntel" in the UA string, so it's only distinguishable from
        // a real Mac by its (non-mouse) multi-touch surface.
        try {
            if (this.#nav?.platform === "MacIntel" && (this.#nav?.maxTouchPoints ?? 0) > 1) {
                return !IOS_BROWSER_EXCLUSION_PATTERN.test(ua);
            }
        }
        catch {
            // Property access can throw in locked-down embedders.
        }
        return false;
    }
    /** True on a real Mac running Safari 17+ — the "Add to Dock" affordance's home. Excludes
     * every other engine that reports a Safari-shaped/Mac-shaped UA (Chrome, Edge, Opera,
     * Firefox all include "Safari" in their UA string on macOS) and excludes iPadOS, which
     * reports `Macintosh` in its UA too but is already handled by `#isIosSafari` via its
     * multi-touch surface — a real Mac has `maxTouchPoints <= 1`. */
    #isMacSafari() {
        try {
            const ua = this.#userAgent;
            if (!MAC_UA_PATTERN.test(ua))
                return false;
            if (!SAFARI_UA_PATTERN.test(ua))
                return false;
            if (NON_SAFARI_ENGINE_PATTERN.test(ua))
                return false;
            const maxTouchPoints = this.#nav?.maxTouchPoints ?? 0;
            return maxTouchPoints <= 1;
        }
        catch {
            return false;
        }
    }
    #safeOrigin() {
        try {
            return this.#win?.location?.origin;
        }
        catch {
            return undefined;
        }
    }
}
export function createBrowserInstallEnvironment(options = {}) {
    return new BrowserInstallEnvironment(options);
}
function safeGlobalWindow() {
    try {
        return typeof window !== "undefined" ? window : undefined;
    }
    catch {
        // Accessing the property itself can throw in some locked-down embedders.
        return undefined;
    }
}
function safeGlobalNavigator() {
    try {
        return typeof navigator !== "undefined" ? navigator : undefined;
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=browser-install-environment.js.map